import React from 'react';
import { GetHistory } from 'app/components/router/History';
import { History } from 'app/services/history';
import { LocationDescriptor } from 'history';
import BaseAnchor from 'app/components/router/BaseAnchor';
import Logger from 'app/services/logger';
import { CallbackRef } from 'app/utils/react';

interface IDeps {
  history: History;
}

export interface ILinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Extend current location, only applies when `to` is an object */
  extend?: boolean;
  replace?: boolean;
  href?: never;
  /**
   * Specify that you want the link to use the hardPush or hardReplace history methods
   * explicitly.
   */
  hard?: boolean;
  to: LocationDescriptor | null; // TODO: Use a tagged union to allow this prop not to be specified
  getRef?: CallbackRef;
}

class Link extends React.Component<ILinkProps & IDeps> {
  public render = () => {
    const {
      getRef,
      to,
      href,
      history,
      /* eslint-disable @typescript-eslint/no-unused-vars */
      replace,
      extend, // don't pass to <a> element
      hard,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...rest
    } = this.props;

    if (!history) {
      Logger.warn('You should not use <Link> outside of <Router>, rendering <a> instead');
      return <BaseAnchor {...this.props} />;
    }

    if (href) {
      Logger.warn('You specified an href which will be ignored');
    }

    if (to === undefined) {
      Logger.warn('You must specify a `to` prop for <Link>');
    }

    // Passing down an href for browser interactions with the <a> element, but
    // it should be impossible to link off site from the <a> element alone.
    // Also necessary for _blank to work correctly
    const anchorHref = this.getHref();

    return (
      <BaseAnchor
        defaultOnClick={this.handleClick}
        getRef={getRef}
        href={anchorHref || undefined} // cannot pass null to href
        {...rest}
      />
    );
  };

  private getHref(): string | null {
    const { history, to, extend } = this.props;
    if (!to) return null;
    if (typeof to === 'string') return to;
    return extend ? history.extendHref(to) : history.createHref(to);
  }

  private handleClick = (e: React.MouseEvent) => {
    // Always prevent default, we don't want to use regular anchor behavior
    e.preventDefault();

    const {
      replace,
      history,
      hard,
      extend,
      to,
    } = this.props;

    const href = this.getHref();
    const {
      hardPush,
      hardReplace,
      smartPush,
      smartReplace,
    } = history;

    // If no `to` is specified, return after preventing default.
    if (!href || !to) {
      return;
    }

    // smartPush and smartReplace can handle can handle both strings LocationDescriptorObjects
    // if extending, prefer the href over the descriptor object
    const smartParam = typeof to === 'object' ? (extend ? href : to) : href;

    // the hard param is used to by pass the smart routing logic in history.
    if (replace) {
      hard ? hardReplace(href) : smartReplace(smartParam);
    } else {
      hard ? hardPush(href) : smartPush(smartParam);
    }
  };
}

export default (props: ILinkProps) => (
  <GetHistory>{history =>
    <Link history={history} {...props} />
  }</GetHistory>
);
